<template>
  <div class="modal fade show" id="depositModal" tabindex="-1" style="display: block; background: rgba(0,0,0,0.5);" v-if="isOpen">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title fw-bold text-slate" id="depositModalLabel">
            {{ isQRGenerated ? 'Thông tin chuyển khoản' : 'Nạp tiền vào tài khoản' }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body py-4">
          <!-- Step 1: Input Amount -->
          <div v-if="!isQRGenerated">
            <div class="form-group mb-4">
              <label class="form-label text-muted small fw-bold">SỐ TIỀN CẦN NẠP (VND)</label>
              <div class="input-group input-group-lg border rounded-3 overflow-hidden custom-input-group">
                <input
                  type="text"
                  class="form-control border-0 bg-light"
                  v-model="formattedAmount"
                  @input="handleAmountInput"
                  placeholder="Ví dụ: 100.000"
                />
                <span class="input-group-text border-0 bg-light fw-bold text-muted">₫</span>
              </div>
              <p v-if="errorMsg" class="text-danger small mt-2 fw-semibold">
                <vue-feather type="alert-circle" size="14" class="me-1"></vue-feather>
                {{ errorMsg }}
              </p>
            </div>

            <div class="quick-amounts d-flex flex-wrap gap-2 mb-4">
              <button
                v-for="amt in quickAmounts"
                :key="amt"
                class="btn btn-outline-light btn-sm text-dark border"
                @click="setAmount(amt)"
              >
                +{{ formatVND(amt) }}
              </button>
            </div>

            <div class="form-group mb-4" v-if="activeBanks.length > 0">
              <label class="form-label text-muted small fw-bold">CHỌN NGÂN HÀNG</label>
              <div class="row g-2">
                <div class="col-6 col-md-4" v-for="bank in activeBanks" :key="bank.id">
                  <div class="bank-card h-100 d-flex flex-column align-items-center justify-content-center p-3"
                       :class="{'selected': selectedBankId === bank.id}"
                       @click="selectedBankId = bank.id">
                    <div class="logo-wrapper mb-2">
                      <img v-if="bank.logo_url" :src="bank.logo_url" alt="logo" />
                      <div v-else class="fw-bold text-primary h4 mb-0">{{ bank.short_name }}</div>
                    </div>
                    <div class="bank-name text-center w-100" :title="bank.short_name || bank.bank_name">
                      {{ bank.short_name || bank.bank_name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              class="btn btn-primary w-100 py-3 fw-bold shadow-sm rounded-3"
              @click="generateQR"
              :disabled="loading || rawAmount < 10000"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? 'Đang khởi tạo...' : 'Tạo mã QR thanh toán' }}
            </button>
          </div>

          <!-- Step 2: Show QR Code -->
          <div v-else class="text-center">
            <div class="qr-container p-3 bg-white rounded-4 shadow-sm border mb-4 d-inline-block">
              <img :src="qrImageUrl" alt="VietQR" class="img-fluid rounded-3" style="max-height: 280px;" />
            </div>

            <div class="instruction-card bg-primary-light border-0 rounded-4 p-3 mb-4 text-start">
              <h6 class="fw-bold text-primary mb-2">Hướng dẫn thanh toán</h6>
              <ul class="list-unstyled mb-0 small text-slate">
                <li class="mb-2 d-flex">
                  <span class="badge bg-primary rounded-circle me-2 d-flex align-items-center justify-content-center" style="width: 18px; height: 18px;">1</span>
                  <span>Mở ứng dụng Ngân hàng (Mobile Banking)</span>
                </li>
                <li class="mb-2 d-flex">
                  <span class="badge bg-primary rounded-circle me-2 d-flex align-items-center justify-content-center" style="width: 18px; height: 18px;">2</span>
                  <span>Chọn tính năng <strong>Quét mã QR</strong></span>
                </li>
                <li class="d-flex">
                  <span class="badge bg-primary rounded-circle me-2 d-flex align-items-center justify-content-center" style="width: 18px; height: 18px;">3</span>
                  <span>Quét mã QR bên trên và xác nhận chuyển khoản</span>
                </li>
              </ul>
            </div>

            <div class="alert alert-warning border-0 rounded-4 p-3 small fw-semibold text-start mb-2">
              <vue-feather type="info" size="14" class="me-2 text-warning font-weight-bold"></vue-feather>
              Quý khách vui lòng chuyển đúng số tiền để hệ thống tự động ghi nhận.
            </div>

            <div class="alert alert-danger border-0 rounded-4 p-3 small fw-semibold text-start mb-0">
              <vue-feather type="alert-triangle" size="14" class="me-2 text-danger font-weight-bold"></vue-feather>
              <strong>Lưu ý:</strong> Tuyệt đối <u>KHÔNG sửa nội dung chuyển khoản</u> — hệ thống dùng mã này để xác nhận giao dịch tự động. Nếu sửa, giao dịch sẽ phải xử lý thủ công.
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0" v-if="isQRGenerated">
          <button type="button" class="btn btn-light w-100 py-2 fw-semibold text-muted" @click="resetModal">Nhập lại số tiền</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, onMounted } from 'vue';
import axios from 'axios';
import { usePayments } from '@/composables/usePayments';
import { useBankAccounts } from '@/composables/useBankAccounts';

const props = defineProps({
  isOpen: Boolean
});

const emit = defineEmits(['close']);

const { createPaymentsApi } = usePayments();
const { fetchActiveBankAccountsApi } = useBankAccounts();

const activeBanks = ref([]);
const selectedBankId = ref(null);

const fetchBanks = async () => {
  const res = await fetchActiveBankAccountsApi();
  if (res.success && res.data?.data) {
    activeBanks.value = res.data.data;
    if (activeBanks.value.length > 0) {
      // Prioritize selecting the default bank
      const defaultBank = activeBanks.value.find(b => b.is_default);
      if (defaultBank) {
        selectedBankId.value = defaultBank.id;
      } else if (!selectedBankId.value) {
        selectedBankId.value = activeBanks.value[0].id;
      }
    }
  }
};

const loading = ref(false);
const isQRGenerated = ref(false);
const rawAmount = ref(0);
const formattedAmount = ref('');
const qrImageUrl = ref('');
const errorMsg = ref('');
const paymentCode = ref('');

const quickAmounts = [50000, 100000, 200000, 500000, 1000000];

const formatVND = (value) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

const handleAmountInput = (e) => {
  let val = e.target.value.replace(/\D/g, '');
  rawAmount.value = parseInt(val) || 0;
  formattedAmount.value = formatVND(rawAmount.value);
  if (rawAmount.value < 10000 && rawAmount.value > 0) {
    errorMsg.value = 'Số tiền tối thiểu là 10.000 ₫';
  } else {
    errorMsg.value = '';
  }
};

const setAmount = (amt) => {
  rawAmount.value = amt;
  formattedAmount.value = formatVND(amt);
  errorMsg.value = '';
};

const generateQR = async () => {
  if (rawAmount.value < 10000) {
    errorMsg.value = 'Số tiền tối thiểu là 10.000 ₫';
    return;
  }

  const selectedBank = activeBanks.value.find(b => b.id === selectedBankId.value);
  if (!selectedBank) {
    errorMsg.value = 'Hệ thống chưa cấu hình tài khoản nhận tiền hoặc bạn chưa chọn ngân hàng. Liên hệ admin.';
    return;
  }

  const accountNo = selectedBank.account_number;
  const accountName = selectedBank.account_name;
  const acqId = selectedBank.acq_id;

  loading.value = true;
  errorMsg.value = '';

  try {
    const createRes = await createPaymentsApi({
      payment_type: 1,
      amount: rawAmount.value,
      bank_account_id: Number(selectedBankId.value),
    });

    const code = createRes?.data?.data?.code;
    if (!createRes.success || !code) {
      errorMsg.value = createRes.error || 'Không thể tạo yêu cầu nạp tiền. Vui lòng thử lại.';
      return;
    }
    paymentCode.value = code;

    const payload = {
      accountNo,
      accountName,
      acqId,
      addInfo: paymentCode.value,
      amount: rawAmount.value,
      template: "compact2"
    };

    const response = await axios.post('https://api.vietqr.io/v2/generate', payload);

    if (response.data?.code === '00') {
      qrImageUrl.value = response.data.data.qrDataURL;
      isQRGenerated.value = true;
      window.dispatchEvent(new CustomEvent('payments:changed', { detail: { reason: 'deposit-created' } }));
    } else {
      errorMsg.value = response.data?.desc || 'Không thể khởi tạo mã QR';
    }
  } catch (err) {
    errorMsg.value = 'Lỗi kết nối đến máy chủ VietQR. Vui lòng thử lại.';
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  emit('close');
};

const resetModal = () => {
  isQRGenerated.value = false;
  qrImageUrl.value = '';
};


watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    resetModal();
    rawAmount.value = 0;
    formattedAmount.value = '';
    errorMsg.value = '';
    paymentCode.value = '';
    fetchBanks();
  }
});

</script>

<style scoped>
.text-slate { color: #1e293b; }
.bg-primary-light { background-color: #f0f7ff; }

.custom-input-group {
  transition: all 0.3s ease;
}
.custom-input-group:focus-within {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.quick-amounts .btn:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.qr-container {
  min-width: 250px;
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.instruction-card {
  border-left: 4px solid #3b82f6;
}

.btn-primary {
  background-color: #3b82f6;
  border-color: #3b82f6;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
}

.modal-content {
  border-radius: 1.25rem;
}

.bank-card {
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.03), 0 1px 2px rgba(0,0,0,0.02);
  min-height: 140px;
}

.bank-card:hover {
  border-color: #3b82f6;
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.1), 0 8px 10px -6px rgba(59, 130, 246, 0.1);
}

.bank-card.selected {
  border-color: #3b82f6;
  border-width: 2px;
  background-color: #f8fbff;
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.2);
}

.logo-wrapper {
  height: 60px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-wrapper img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.bank-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
